import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../index.css';

function NotificationBell() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            loadNotifications();
            // Poll for new notifications every 30 seconds
            const interval = setInterval(loadNotifications, 30000);
            return () => clearInterval(interval);
        }
    }, [isAuthenticated]);

    const loadNotifications = async () => {
        try {
            // Only fetch unread notifications
            const response = await axios.get('http://localhost:5000/api/notifications?unreadOnly=true');
            setNotifications(response.data.notifications || []);
            setUnreadCount(response.data.unreadCount || 0);
        } catch (error) {
            console.error('Failed to load notifications:', error);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await axios.put(`http://localhost:5000/api/notifications/${notificationId}/read`);
            loadNotifications();
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            setLoading(true);
            await axios.put('http://localhost:5000/api/notifications/read-all');
            loadNotifications();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationClick = async (notification) => {
        // Mark as read
        if (!notification.isRead) {
            await markAsRead(notification.id);
        }
        // Navigate to the invoice
        if (notification.invoiceId) {
            const invoiceId = notification.invoiceId?.id || notification.invoiceId;
            setShowDropdown(false);
            navigate(`/invoice/edit/${invoiceId}`);
        }
    };

    // Parse notification message to style different parts
    const renderStyledMessage = (notification) => {
        const message = notification.message;
        const invoiceName = notification.invoiceId?.customerName || notification.invoiceId?.companyName;
        const invoiceId = notification.invoiceId?.id || notification.invoiceId;

        // Try to extract user name from message (pattern: "by UserName")
        const byMatch = message.match(/by ([^.]+)$/);
        const userName = byMatch ? byMatch[1] : null;

        // Split message into parts
        let content = message;

        return (
            <div style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                {/* Invoice name - Dark & Clickable */}
                {invoiceName && invoiceId && (
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDropdown(false);
                            navigate(`/invoice/edit/${invoiceId}`);
                        }}
                        style={{
                            color: '#1e40af',
                            fontWeight: '700',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            display: 'inline'
                        }}
                    >
                        {invoiceName}
                    </span>
                )}

                {/* Message content - Light */}
                <span style={{ color: '#6b7280', marginLeft: invoiceName ? '4px' : 0 }}>
                    {invoiceName ? ' - ' : ''}
                    {getActionText(notification.type)}
                </span>

                {/* User name - Medium */}
                {userName && (
                    <span style={{ color: '#374151', fontWeight: '600' }}>
                        {' by '}
                        <span style={{ color: '#4b5563' }}>{userName}</span>
                    </span>
                )}
            </div>
        );
    };

    // Get action text based on notification type
    const getActionText = (type) => {
        switch (type) {
            case 'invoice-created':
                return 'submitted for review';
            case 'payment-requested':
                return 'payment request sent, ready for SO';
            case 'so-created':
                return 'completed - SO Creation Initiated';
            case 'invoice-assigned':
                return 'assigned to you';
            default:
                return 'updated';
        }
    };

    if (!isAuthenticated) return null;

    return (
        <div className="notification-bell-container">
            <button
                className="notification-bell-button"
                onClick={() => setShowDropdown(!showDropdown)}
            >
                🔔
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
            </button>

            {showDropdown && (
                <div className="notification-dropdown">
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '1rem',
                        borderBottom: '1px solid #e5e7eb',
                        background: '#f9fafb'
                    }}>
                        <h3 style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>
                            🔔 Notifications {unreadCount > 0 && `(${unreadCount})`}
                        </h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                disabled={loading}
                                style={{
                                    fontSize: '0.8rem',
                                    color: '#667eea',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Mark all read
                            </button>
                        )}
                    </div>

                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {notifications.length === 0 ? (
                            <div style={{
                                padding: '2rem',
                                textAlign: 'center',
                                color: '#9ca3af'
                            }}>
                                No notifications yet
                            </div>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    style={{
                                        padding: '0.875rem 1rem',
                                        borderBottom: '1px solid #f3f4f6',
                                        background: notification.isRead ? '#ffffff' : '#eff6ff',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '0.75rem'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = notification.isRead ? '#f9fafb' : '#dbeafe'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = notification.isRead ? '#ffffff' : '#eff6ff'}
                                >
                                    {/* Unread indicator */}
                                    <div style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        background: notification.isRead ? 'transparent' : '#3b82f6',
                                        marginTop: '6px',
                                        flexShrink: 0
                                    }}></div>

                                    <div style={{ flex: 1 }}>
                                        {renderStyledMessage(notification)}
                                        <div style={{
                                            fontSize: '0.75rem',
                                            color: '#9ca3af',
                                            marginTop: '0.375rem'
                                        }}>
                                            {notification.createdAtIST || new Date(notification.createdAt).toLocaleString()}
                                        </div>
                                    </div>

                                    {/* Click indicator */}
                                    <div style={{
                                        color: '#9ca3af',
                                        fontSize: '0.8rem'
                                    }}>
                                        →
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {showDropdown && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 998
                    }}
                    onClick={() => setShowDropdown(false)}
                ></div>
            )}
        </div>
    );
}

export default NotificationBell;
